import * as React from 'react';
import { useFormModal } from '@repo/form';
import { FormModal } from '@repo/web-form';
import { useStore } from '@tanstack/react-form';
import { FormField, Input } from '../../../components/ui/FormField';
import { FormSelect } from '../../../components/ui/FormSelect';
import { useRecordPayment, type DueRow, type PaymentMode } from '../network/dues.api';
import { formatMoney } from '@repo/utils';
import { useToast } from '../../../components/ui/Toast';
import { DEFAULT_CURRENCY, DEFAULT_LOCALE } from '../../../lib/locale';
import { Decimal } from 'decimal.js';
import { z } from 'zod';

const PAYMENT_MODE_OPTIONS = [
  { value: 'CASH', label: 'Cash' },
  { value: 'UPI', label: 'UPI' },
  { value: 'NEFT', label: 'NEFT / IMPS' },
  { value: 'CHEQUE', label: 'Cheque' },
  { value: 'ADJUSTMENT', label: 'Adjustment' },
];

const schema = z.object({
  amount: z.string().min(1, 'Amount is required'),
  mode: z.enum(['CASH', 'UPI', 'NEFT', 'CHEQUE', 'ADJUSTMENT']),
  upiRef: z.string().optional(),
  chequeNumber: z.string().optional(),
  chequeDate: z.string().optional(),
  bankName: z.string().optional(),
});

interface RecordPaymentModalProps {
  due: DueRow;
  open: boolean;
  onClose: () => void;
  onSuccess: (receiptNumber: string) => void;
}

export function RecordPaymentModal({ due, open, onClose, onSuccess }: RecordPaymentModalProps) {
  const remaining = due.netDue - due.paidAmount;
  const mutation = useRecordPayment(due.id);
  const { toast } = useToast();

  const modal = useFormModal(
    {
      defaultValues: {
        amount: String(remaining / 100),
        mode: 'CASH' as PaymentMode,
        upiRef: '',
        chequeNumber: '',
        chequeDate: '',
        bankName: '',
      },
      schema,
    },
    mutation,
    {
      onBeforeSubmit: (value) => {
        const amountPaise = new Decimal(value.amount || 0).mul(100).round().toNumber();
        return {
          amount: amountPaise,
          paymentMode: value.mode,
          upiRef: value.mode === 'UPI' || value.mode === 'NEFT' ? value.upiRef : undefined,
          chequeNumber: value.mode === 'CHEQUE' ? value.chequeNumber : undefined,
          chequeDate: value.mode === 'CHEQUE' ? value.chequeDate : undefined,
          bankName: value.mode === 'CHEQUE' ? value.bankName : undefined,
        };
      },
      onSuccess: (result) => {
        toast({ type: 'success', title: 'Payment Recorded', description: 'Receipt generated successfully' });
        onSuccess(result.receiptNumber);
      },
      onError: (e) => {
        toast({ type: 'error', title: 'Error', description: e?.message ?? 'Failed to record payment' });
      },
    },
  );

  const currentMode = useStore(modal.form.store, (s: any) => s.values.mode);

  React.useEffect(() => {
    if (open) {
      modal.form.reset();
      modal.form.setFieldValue('amount', String(remaining / 100));
    }
  }, [open, remaining]);

  return (
    <FormModal open={open} onClose={onClose} title="Record Payment" modal={modal} submitLabel="Record payment">
      <div className="space-y-1 text-sm text-text-secondary mb-2">
        <div className="flex justify-between">
          <span>Net due</span>
          <span className="tnum text-text-primary">{formatMoney(due.netDue, { currency: DEFAULT_CURRENCY, locale: DEFAULT_LOCALE })}</span>
        </div>
        <div className="flex justify-between">
          <span>Already paid</span>
          <span className="tnum text-text-primary">{formatMoney(due.paidAmount, { currency: DEFAULT_CURRENCY, locale: DEFAULT_LOCALE })}</span>
        </div>
        <div className="flex justify-between font-medium text-text-primary border-t border-border pt-1 mt-1">
          <span>Remaining</span>
          <span className="tnum">{formatMoney(remaining, { currency: DEFAULT_CURRENCY, locale: DEFAULT_LOCALE })}</span>
        </div>
      </div>

      <div className="space-y-3">
        <modal.form.Field
          name="amount"
          validators={{
            onChange: ({ value }: { value: any }) => {
              if (!value) return 'Amount is required';
              const amountPaise = new Decimal(value || 0).mul(100).round().toNumber();
              if (isNaN(amountPaise) || amountPaise <= 0) return 'Enter a valid amount';
              if (amountPaise > remaining) return `Amount cannot exceed remaining due of ${formatMoney(remaining, { currency: DEFAULT_CURRENCY, locale: DEFAULT_LOCALE })}`;
              return undefined;
            }
          }}
          children={(field: any) => (
            <FormField
              label="Amount"
              required
              htmlFor="pay-amount"
              error={modal.fieldError(field.state.meta)}
            >
              <Input
                id="pay-amount"
                type="number"
                min="0.01"
                step="0.01"
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                error={field.state.meta.errors.length > 0 && modal.submissionAttempts > 0}
                disabled={modal.isSubmitting}
              />
            </FormField>
          )}
        />

        <modal.form.Field
          name="mode"
          children={(field: any) => (
            <FormSelect
              label="Payment mode"
              required
              options={PAYMENT_MODE_OPTIONS}
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value as PaymentMode)}
              onBlur={field.handleBlur}
              disabled={modal.isSubmitting}
            />
          )}
        />

        {currentMode === 'UPI' && (
          <modal.form.Field
            name="upiRef"
            validators={{
              onChange: ({ value }: { value: any }) => !value?.trim() ? 'UPI reference is required' : undefined
            }}
            children={(field: any) => (
              <FormField
                label="UPI reference"
                required
                htmlFor="upi-ref"
                error={modal.fieldError(field.state.meta)}
              >
                <Input
                  id="upi-ref"
                  type="text"
                  placeholder="12-digit UTR or UPI ref"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  error={field.state.meta.errors.length > 0 && modal.submissionAttempts > 0}
                  disabled={modal.isSubmitting}
                />
              </FormField>
            )}
          />
        )}

        {currentMode === 'NEFT' && (
          <modal.form.Field
            name="upiRef"
            children={(field: any) => (
              <FormField
                label="Reference number"
                htmlFor="neft-ref"
                error={modal.fieldError(field.state.meta)}
              >
                <Input
                  id="neft-ref"
                  type="text"
                  placeholder="NEFT / IMPS reference"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  disabled={modal.isSubmitting}
                />
              </FormField>
            )}
          />
        )}

        {currentMode === 'CHEQUE' && (
          <>
            <modal.form.Field
              name="chequeNumber"
              validators={{
                onChange: ({ value }: { value: any }) => !value?.trim() ? 'Cheque number is required' : undefined
              }}
              children={(field: any) => (
                <FormField
                  label="Cheque number"
                  required
                  htmlFor="cheque-no"
                  error={modal.fieldError(field.state.meta)}
                >
                  <Input
                    id="cheque-no"
                    type="text"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    error={field.state.meta.errors.length > 0 && modal.submissionAttempts > 0}
                    disabled={modal.isSubmitting}
                  />
                </FormField>
              )}
            />
            <modal.form.Field
              name="chequeDate"
              validators={{
                onChange: ({ value }: { value: any }) => !value ? 'Cheque date is required' : undefined
              }}
              children={(field: any) => (
                <FormField
                  label="Cheque date"
                  required
                  htmlFor="cheque-date"
                  error={modal.fieldError(field.state.meta)}
                >
                  <Input
                    id="cheque-date"
                    type="date"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    error={field.state.meta.errors.length > 0 && modal.submissionAttempts > 0}
                    disabled={modal.isSubmitting}
                  />
                </FormField>
              )}
            />
            <modal.form.Field
              name="bankName"
              validators={{
                onChange: ({ value }: { value: any }) => !value?.trim() ? 'Bank name is required' : undefined
              }}
              children={(field: any) => (
                <FormField
                  label="Bank name"
                  required
                  htmlFor="bank-name"
                  error={modal.fieldError(field.state.meta)}
                >
                  <Input
                    id="bank-name"
                    type="text"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    error={field.state.meta.errors.length > 0 && modal.submissionAttempts > 0}
                    disabled={modal.isSubmitting}
                  />
                </FormField>
              )}
            />
          </>
        )}
      </div>
    </FormModal>
  );
}
